/**
 * PlateTrendChart (iter1071) — как се движи чинията по дни.
 * `compact` = плочка за таблото; иначе пълен раздел с избор на показател.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { useTheme } from "@/theme/ThemeContext";
import { Loader2, TrendingDown, TrendingUp, Minus, Utensils, ArrowRight } from "lucide-react";

const ORDER = ["dil", "sat_fat_g", "omega_ratio", "mineral_over"];

const TREND_STYLE = {
  better: { Icon: TrendingDown, cls: "text-emerald-600 dark:text-emerald-400" },
  worse: { Icon: TrendingUp, cls: "text-rose-600 dark:text-rose-400" },
  flat: { Icon: Minus, cls: "text-slate-500" },
  unknown: { Icon: Minus, cls: "text-slate-400" },
};

const dayLabel = (iso) => `${Number(iso.slice(8, 10))}.${Number(iso.slice(5, 7))}`;

export default function PlateTrendChart({ compact = false, days = 30 }) {
  const { isDark } = useTheme();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("dil");

  useEffect(() => {
    axios.get(`${API}/plate-simulator/trend?days=${compact ? 14 : days}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API, days, compact]);

  const chart = useMemo(
    () => (data?.series || [])
      .filter((p) => p[metric] !== null)
      .map((p) => ({ ...p, label: dayLabel(p.date) })),
    [data, metric],
  );

  const card = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
    : "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm";

  if (loading) {
    return <div className={card} data-testid="plate-trend-loading">
      <Loader2 className="w-4 h-4 animate-spin opacity-60" /></div>;
  }

  if (!data?.has_data) {
    return (
      <div className={card} data-testid="plate-trend-empty">
        <div className="flex items-center gap-2 mb-1">
          <Utensils className="w-4 h-4 text-emerald-600" />
          <h3 className="text-base md:text-lg font-bold">Как се движи чинията</h3>
        </div>
        <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Още няма записани хранения. Съставете едно в симулатора на чинията и
          графиката ще започне да се пълни.
        </p>
        <Link to="/dashboard/plate-simulator" data-testid="plate-trend-open-simulator"
          className="inline-flex items-center gap-1.5 text-sm font-semibold mt-3 text-emerald-700 dark:text-emerald-400">
          Отворете симулатора <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const badges = ORDER.map((k) => ({ key: k, ...data.metrics[k] }));
  const active = data.metrics[metric];

  return (
    <div className={card} data-testid={compact ? "plate-trend-tile" : "plate-trend-section"}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Utensils className="w-4 h-4 text-emerald-600 shrink-0" />
            <h3 className="text-base md:text-lg font-bold">Как се движи чинията</h3>
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {data.logged_days} записани дни · {data.total_meals} хранения за последните {data.days} дни
          </p>
        </div>
        {compact && (
          <Link to="/dashboard/plate-simulator" data-testid="plate-trend-tile-cta"
            className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 shrink-0">
            Подробно →
          </Link>
        )}
      </div>

      {/* Посока по показатели */}
      <div className={`grid gap-2 mt-4 ${compact ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-4"}`}
        data-testid="plate-trend-badges">
        {badges.map((b) => {
          const { Icon, cls } = TREND_STYLE[b.trend] || TREND_STYLE.unknown;
          return (
            <button key={b.key} type="button"
              onClick={() => !compact && setMetric(b.key)}
              data-testid={`plate-trend-metric-${b.key}`}
              className={`text-left rounded-xl border p-2.5 transition-colors ${
                !compact && metric === b.key
                  ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                  : isDark ? "border-slate-800 hover:bg-slate-800/50"
                    : "border-slate-200 hover:bg-slate-50"}`}>
              <div className={`text-[11px] font-semibold ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {b.label_bg}
              </div>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="text-lg font-bold">
                  {b.last_7_avg === null ? "—" : b.last_7_avg}
                </span>
                <span className="text-[10px] opacity-60">{b.unit}</span>
              </div>
              <div className={`flex items-center gap-1 text-[11px] font-semibold mt-0.5 ${cls}`}>
                <Icon className="w-3 h-3" /> {b.trend_label_bg}
                {b.change_pct !== null && b.trend !== "flat" && b.trend !== "unknown"
                  && ` ${Math.abs(b.change_pct)}%`}
              </div>
            </button>
          );
        })}
      </div>

      {/* Графика */}
      <div className={compact ? "h-28 mt-3" : "h-64 mt-5"} data-testid="plate-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3"
              stroke={isDark ? "#1e293b" : "#e2e8f0"} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke={isDark ? "#64748b" : "#94a3b8"}
              interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} stroke={isDark ? "#64748b" : "#94a3b8"} width={36} />
            <Tooltip
              contentStyle={{
                background: isDark ? "#0f172a" : "#ffffff",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                borderRadius: 12, fontSize: 12,
              }}
              formatter={(v) => [`${v} ${active.unit}`, active.label_bg]}
              labelFormatter={(l) => `Ден ${l}`} />
            {active.limit > 0 && (
              <ReferenceLine y={active.limit} stroke="#f59e0b" strokeDasharray="4 4"
                label={{ value: compact ? "" : `Лимит ${active.limit}`,
                  fontSize: 10, fill: "#f59e0b", position: "insideTopRight" }} />
            )}
            <Line type="monotone" dataKey={metric} stroke="#059669" strokeWidth={2}
              dot={{ r: compact ? 0 : 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {!compact && (
        <p className={`text-xs mt-3 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          {data.note_bg}
        </p>
      )}
    </div>
  );
}
